import { notify } from '@affine/component';
import type { ContainerCopyBeforeDeleteService } from '@affine/core/components/delete-with-copy/container-copy-before-delete.service';
import {
  type AppBaseTableDispositionInput,
  deleteAppBaseMutation,
  permanentlyDeleteAppBaseMutation,
  restoreAppBaseMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { OnEvent, Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import {
  classifyOfflineMutationError,
  OFFLINE_DELETE_COPY_CONFIRMATION,
  OFFLINE_DELETE_TABLE_CHOICE,
  PERMANENT_DELETE_DOMAIN_CODES,
} from '../../offline-mutation-outbox/services/offline-mutation-error';
import {
  OfflineMutationDomainError,
  type OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import {
  OFFLINE_DELETION_HOLD_MS,
  type OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { AccessibleFolderCacheStore } from '../../organize';
import type { Workspace } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import { hasAppBaseErrorCode } from '../hooks/app-base-error-code';

type AppLifecycleRecord = OfflineMutationRecord<
  'appBase.trash' | 'appBase.restore' | 'appBase.permanentDelete'
>;

@OnEvent(
  WorkspaceInitialized,
  (service: AppBaseLifecycleOutboxService) => service.onWorkspaceInitialized
)
export class AppBaseLifecycleOutboxService extends Service {
  constructor(
    private readonly workspaceServer: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly folders: AccessibleFolderCacheStore,
    private readonly docs: AccessibleDocCacheStore,
    private readonly copyBeforeDelete: ContainerCopyBeforeDeleteService
  ) {
    super();
    this.disposables.push(
      outbox.registerExecutor('appBase.trash', record => this.send(record)),
      outbox.registerExecutor('appBase.restore', record => this.send(record)),
      outbox.registerExecutor('appBase.permanentDelete', record =>
        this.send(record)
      )
    );
    const subscription = outbox.failed$.subscribe(record => {
      if (
        (record.operation !== 'appBase.trash' &&
          record.operation !== 'appBase.restore' &&
          record.operation !== 'appBase.permanentDelete') ||
        record.failure !== 'refused' ||
        record.lastError === OFFLINE_DELETE_COPY_CONFIRMATION ||
        record.lastError === OFFLINE_DELETE_TABLE_CHOICE
      )
        return;
      notify.error({
        title: I18n.t(
          record.operation === 'appBase.permanentDelete'
            ? 'com.streamlain.appBases.error.permanentDelete'
            : record.operation === 'appBase.trash'
              ? 'com.streamlain.appBases.error.delete'
              : 'com.streamlain.trash.restore-failed'
        ),
        message: record.lastError,
      });
      outbox.removeFailed(record.id).catch(console.error);
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    // Construct the executors when the workspace opens, including after reload.
  }

  requestTrash(appBaseId: string): Promise<{ mutationId: string }> {
    return this.enqueue(appBaseId, 'appBase.trash');
  }

  requestRestore(appBaseId: string): Promise<{ mutationId: string }> {
    return this.enqueue(appBaseId, 'appBase.restore');
  }

  /** `tables` is the choice for each table of its own; without it the server asks again. */
  requestPermanentDelete(
    appBaseId: string,
    copyConfirmed = false,
    tables?: AppBaseTableDispositionInput[]
  ): Promise<{ mutationId: string }> {
    return this.enqueue(
      appBaseId,
      'appBase.permanentDelete',
      copyConfirmed,
      tables
    );
  }

  private async enqueue(
    appBaseId: string,
    operation: AppLifecycleRecord['operation'],
    copyConfirmed = false,
    tables?: AppBaseTableDispositionInput[]
  ): Promise<{ mutationId: string }> {
    const record = await this.outbox.enqueue({
      operation,
      payload: { appBaseId, copyConfirmed, ...(tables && { tables }) },
      idempotencyKey: `${operation}:${appBaseId}`,
      dedupePending: true,
      holdMs:
        operation !== 'appBase.restore' ? OFFLINE_DELETION_HOLD_MS : undefined,
      orderKey: `appBase:${appBaseId}`,
    });
    return { mutationId: record.id };
  }

  private async send(record: AppLifecycleRecord): Promise<void> {
    try {
      if (record.operation === 'appBase.permanentDelete')
        await this.copyBeforeDelete.checkBeforeSend(
          record as OfflineMutationRecord<'appBase.permanentDelete'>
        );
      const server = this.workspaceServer.server;
      if (!server) throw new OfflineMutationTransientError('No server');
      const variables = {
        workspaceId: record.workspaceId,
        id: record.payload.appBaseId,
      };
      if (record.operation === 'appBase.permanentDelete') {
        const { tables } = (
          record as OfflineMutationRecord<'appBase.permanentDelete'>
        ).payload;
        await server.gql({
          query: permanentlyDeleteAppBaseMutation,
          variables: { ...variables, tables: tables ?? [] },
        });
      } else {
        await server.gql({
          query:
            record.operation === 'appBase.trash'
              ? deleteAppBaseMutation
              : restoreAppBaseMutation,
          variables,
        });
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === OFFLINE_DELETE_COPY_CONFIRMATION
      )
        throw error;
      if (
        record.operation === 'appBase.permanentDelete' &&
        hasAppBaseErrorCode(error, OFFLINE_DELETE_TABLE_CHOICE)
      )
        throw new OfflineMutationDomainError(OFFLINE_DELETE_TABLE_CHOICE);
      throw classifyOfflineMutationError(
        error,
        record.operation === 'appBase.permanentDelete'
          ? PERMANENT_DELETE_DOMAIN_CODES
          : ['FORBIDDEN', 'BAD_REQUEST', 'NOT_FOUND']
      );
    }
    await Promise.all([
      this.folders.fetchAccessibleFolders(),
      ...(record.operation === 'appBase.permanentDelete'
        ? [this.folders.fetchTrashedFolders()]
        : []),
      this.docs.fetchAccessibleDocs(),
    ]);
  }
}
